import React from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { EditIcon, DeleteIcon } from '../components/icons';

const EMPTY_FORM = { company_id: '', state_id: '', gst_number: '', invoice_code: '', office_address: '' };

class States extends React.Component {
  state = {
    registrations: [],
    companies: [],
    states: [],
    loading: true,
    error: '',
    saving: false,
    showModal: false,
    editing: null,
    form: EMPTY_FORM
  };

  componentDidMount() {
    this.loadAll();
  }

  loadAll = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const [regRes, companiesRes, statesRes] = await Promise.all([
        api.get('/company-states'),
        api.get('/companies'),
        api.get('/states')
      ]);
      this.setState({
        registrations: regRes.data,
        companies: companiesRes.data,
        states: statesRes.data,
        loading: false
      });
    } catch (err) {
      this.setState({ error: 'Failed to load state registrations.', loading: false });
    }
  };

  openAddModal = () => this.setState({ showModal: true, editing: null, form: EMPTY_FORM, error: '' });

  openEditModal = (reg) => {
    this.setState({
      showModal: true,
      editing: reg,
      error: '',
      form: {
        company_id: reg.company_id,
        state_id: reg.state_id,
        gst_number: reg.gst_number || '',
        invoice_code: reg.invoice_code || '',
        office_address: reg.office_address || ''
      }
    });
  };

  closeModal = () => this.setState({ showModal: false });

  handleFormChange = (e) => {
    this.setState({ form: { ...this.state.form, [e.target.name]: e.target.value }, error: '' });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { editing, form } = this.state;

    if (!form.company_id || !form.state_id || !form.gst_number) {
      this.setState({ error: 'Company, state, and GST number are required.' });
      return;
    }

    this.setState({ saving: true, error: '' });
    try {
      if (editing) {
        await api.put(`/company-states/${editing.id}`, form);
      } else {
        await api.post('/company-states', form);
      }
      this.closeModal();
      this.loadAll();
    } catch (err) {
      const message = err.response?.data?.message || 'Save failed. Check the values and try again.';
      this.setState({ error: message });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm('Delete this GST registration?')) return;
    try {
      await api.delete(`/company-states/${id}`);
      this.loadAll();
    } catch (err) {
      this.setState({ error: 'Delete failed.' });
    }
  };

  render() {
    const { registrations, companies, states, loading, error, showModal, saving, editing, form } = this.state;

    return (
      <Layout>
        <div className="page-header">
          <div>
            <h1 className="page-title">State of Company</h1>
            <p className="page-sub">State-wise GST registrations per company</p>
          </div>
          <button className="btn btn-brass" onClick={this.openAddModal}>+ Add State</button>
        </div>

        {error && !showModal && <div className="error-banner">{error}</div>}

        <div className="card">
          {loading ? (
            <p>Loading...</p>
          ) : registrations.length === 0 ? (
            <div className="empty-state">
              <div className="glyph">—</div>
              No state registrations added yet.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>State</th>
                  <th>GST No.</th>
                  <th>Invoice Code</th>
                  <th>Office Address</th>
                  <th className="actions-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.id}>
                    <td data-label="Company">{r.company_name}</td>
                    <td data-label="State">{r.state_name} <span className="mono" style={{ color: 'var(--muted)' }}>({r.state_code})</span></td>
                    <td className="mono" data-label="GST No.">{r.gst_number}</td>
                    <td className="mono" data-label="Invoice Code">{r.invoice_code || '—'}</td>
                    <td data-label="Office Address">{r.office_address || '—'}</td>
                    <td className="actions-cell" data-label="Action">
                      <div className="row-actions">
                        <button className="action-btn edit" title="Edit" onClick={() => this.openEditModal(r)}>
                          <EditIcon />
                        </button>
                        <button className="action-btn delete" title="Delete" onClick={() => this.handleDelete(r.id)}>
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="modal-backdrop" onClick={this.closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editing ? 'Edit State Registration' : 'Add State'}</h2>

              {error && <div className="error-banner">{error}</div>}

              <form onSubmit={this.handleSubmit}>
                <div className="field">
                  <label>Company</label>
                  <select name="company_id" value={form.company_id} onChange={this.handleFormChange} required>
                    <option value="">Select company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="field">
                    <label>State Name</label>
                    <select name="state_id" value={form.state_id} onChange={this.handleFormChange} required>
                      <option value="">Select state</option>
                      {states.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>State Code</label>
                    <input
                      value={states.find((s) => String(s.id) === String(form.state_id))?.code || ''}
                      disabled
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="field">
                    <label>GST No.</label>
                    <input name="gst_number" value={form.gst_number} onChange={this.handleFormChange} required />
                  </div>
                  <div className="field">
                    <label>Code for Invoice</label>
                    <input
                      name="invoice_code"
                      value={form.invoice_code}
                      onChange={this.handleFormChange}
                      placeholder="e.g. MH01"
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Office Address</label>
                  <textarea name="office_address" rows={2} value={form.office_address} onChange={this.handleFormChange} />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-brass" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    );
  }
}

export default States;